/**
 * SymbolPalette — left sidebar of all available symbols, grouped by
 * category and draggable onto the canvas.
 *
 * Search input at the top filters by label / category / id. When
 * filtering, the category headers stay so you can still tell what
 * kind of thing you're looking at, but groups with no matches are
 * hidden. Empty results show a friendly hint.
 */
import { useMemo, useState } from 'react';
import { symbolsForPalette, renderPanelString } from '../symbols/library';
import type { SymbolDef } from '../types/symbol';

const CATEGORY_LABELS: Record<string, string> = {
  inverter: 'Power Conversion',
  pv: 'PV',
  storage: 'Storage',
  switchgear: 'Switchgear',
  protection: 'Protection',
  metering: 'Metering',
  utility: 'Utility',
  misc: 'Misc',
};

// panelString carries no static SVG (content is generated per-instance
// via renderPanelString). Pre-bake a tiny 4-panel preview once at
// module load so the palette gets a representative thumbnail instead
// of an empty box.
const PANEL_STRING_PREVIEW = renderPanelString(4);

function SymbolPreview({ sym }: { sym: SymbolDef }) {
  const maxSize = 48;
  // Special-case panelString — its real content is dynamic, so we
  // render a small 4-panel composite as the palette icon. Same
  // visual language as the on-canvas blocks.
  if (sym.id === 'panelString') {
    const nb = PANEL_STRING_PREVIEW.nativeBox;
    const scale = Math.min(maxSize / nb.w, maxSize / nb.h) * 0.95;
    const renderW = nb.w * scale;
    const renderH = nb.h * scale;
    return (
      <svg
        width={maxSize}
        height={maxSize}
        viewBox={`0 0 ${maxSize} ${maxSize}`}
        style={{ pointerEvents: 'none' }}
      >
        <g
          transform={`translate(${(maxSize - renderW) / 2}, ${(maxSize - renderH) / 2}) scale(${scale})`}
          dangerouslySetInnerHTML={{ __html: PANEL_STRING_PREVIEW.content }}
        />
      </svg>
    );
  }

  const nb = sym.nativeBox;
  // Slightly bigger preview area than before — easier to recognise
  // a 12-pixel fuse symbol when the thumbnail is ~50px.
  const scale = Math.min(maxSize / nb.w, maxSize / nb.h) * 0.9;
  const renderW = nb.w * scale;
  const renderH = nb.h * scale;

  return (
    <svg
      width={maxSize}
      height={maxSize}
      viewBox={`0 0 ${maxSize} ${maxSize}`}
      style={{ pointerEvents: 'none' }}
    >
      <g
        transform={`translate(${(maxSize - renderW) / 2}, ${(maxSize - renderH) / 2}) scale(${scale})`}
        dangerouslySetInnerHTML={{ __html: sym.content }}
      />
    </svg>
  );
}

export function SymbolPalette() {
  const groups = symbolsForPalette();
  const [query, setQuery] = useState('');

  const onDragStart = (event: React.DragEvent, symbolId: string) => {
    event.dataTransfer.setData('application/reactflow-sld-symbol', symbolId);
    event.dataTransfer.effectAllowed = 'move';
  };

  // Apply the search filter once per query change. Match against
  // both the symbol's own label/id AND the category name so typing
  // "switch" surfaces the whole switchgear group.
  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => {
        const catLabel = (CATEGORY_LABELS[g.category] ?? g.category).toLowerCase();
        const catMatches = catLabel.includes(q);
        return {
          ...g,
          symbols: catMatches
            ? g.symbols
            : g.symbols.filter((s) =>
                `${s.label} ${s.id}`.toLowerCase().includes(q)
              ),
        };
      })
      .filter((g) => g.symbols.length > 0);
  }, [groups, query]);

  const totalShown = filteredGroups.reduce((n, g) => n + g.symbols.length, 0);

  return (
    <div className="palette">
      <div className="palette-search">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search symbols…"
          aria-label="Search symbols"
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      <div className="palette-body">
        {totalShown === 0 ? (
          <div className="palette-empty">
            No symbols match <strong>{query}</strong>
          </div>
        ) : (
          filteredGroups.map((group) => (
            <div key={group.category} className="palette-group">
              <div className="palette-header">
                {CATEGORY_LABELS[group.category] ?? group.category}
              </div>
              <div className="palette-items">
                {group.symbols.map((sym) => (
                  <div
                    key={sym.id}
                    className="palette-item"
                    draggable
                    onDragStart={(e) => onDragStart(e, sym.id)}
                    title={`${sym.label} (${sym.source}) — drag onto the canvas`}
                  >
                    <SymbolPreview sym={sym} />
                    <div className="palette-item-label">{sym.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
